import { authManager } from './auth-manager';
import { profileManager } from './profile-manager';

type Role = 'user' | 'creator' | 'admin';
type Permission = string;

/**
 * Permission Manager - Handles role-based access control
 * Features:
 * - Fast permission checks (no DB calls)
 * - Feature flag support
 * - Extensible permission matrix
 */
export class PermissionManager {
  // Permission matrix - defines what each role can do
  private static readonly PERMISSIONS: Record<Role, Set<Permission>> = {
    user: new Set([
      'profile.view',
      'profile.edit.own',
      'experiences.view',
      'experiences.book',
      'bookings.view.own',
      'bookings.cancel.own',
      'messages.send',
      'messages.view.own'
    ]),
    
    creator: new Set([
      // Inherit all user permissions
      'profile.view',
      'profile.edit.own',
      'experiences.view',
      'experiences.book',
      'bookings.view.own',
      'bookings.cancel.own',
      'messages.send',
      'messages.view.own',
      // Creator-specific permissions
      'creator.dashboard.view',
      'experiences.create',
      'experiences.edit.own',
      'experiences.delete.own',
      'bookings.view.for_experiences',
      'bookings.approve',
      'bookings.reject',
      'analytics.view.own',
      'stripe.connect',
      'customers.view',
      'customers.export'
    ]),
    
    admin: new Set([
      // Admin has all permissions
      '*'
    ])
  };

  private cachedRole: Role | null = null;
  private cacheTimestamp = 0;
  private readonly CACHE_TTL = 60 * 1000; // 1 minute

  /**
   * Check if current user has a specific permission
   */
  async hasPermission(permission: Permission): Promise<boolean> {
    const role = await this.getRole();
    if (!role) return false;

    const permissions = PermissionManager.PERMISSIONS[role];
    
    // Admin wildcard check
    if (permissions.has('*')) return true;
    
    // Direct permission check
    if (permissions.has(permission)) return true;
    
    // Wildcard permission check (e.g., 'experiences.*')
    const permissionParts = permission.split('.');
    for (let i = permissionParts.length - 1; i >= 0; i--) {
      const wildcardPermission = [...permissionParts.slice(0, i), '*'].join('.');
      if (permissions.has(wildcardPermission)) return true;
    }
    
    return false;
  }

  /**
   * Check if current user has all specified permissions
   */
  async hasAllPermissions(...permissions: Permission[]): Promise<boolean> {
    for (const permission of permissions) {
      if (!(await this.hasPermission(permission))) {
        return false;
      }
    }
    return true;
  }

  /**
   * Check if current user has any of the specified permissions
   */
  async hasAnyPermission(...permissions: Permission[]): Promise<boolean> {
    for (const permission of permissions) {
      if (await this.hasPermission(permission)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Get current user's role (with caching)
   */
  async getRole(): Promise<Role | null> {
    // Check if we have a valid cached role
    const now = Date.now();
    if (this.cachedRole && (now - this.cacheTimestamp) < this.CACHE_TTL) {
      return this.cachedRole;
    }

    // First check if authenticated
    if (!authManager.isAuthenticated()) {
      this.cachedRole = null;
      return null;
    }

    // Get role from profile
    const role = await profileManager.getRole();
    
    // Cache the result
    this.cachedRole = role;
    this.cacheTimestamp = now;
    
    return role;
  }

  /**
   * Check if user has a specific role
   */
  async hasRole(role: Role): Promise<boolean> {
    const currentRole = await this.getRole();
    return currentRole === role;
  }

  /**
   * Check if user is admin
   */
  async isAdmin(): Promise<boolean> {
    return this.hasRole('admin');
  }

  /**
   * Check if user is creator
   */
  async isCreator(): Promise<boolean> {
    return this.hasRole('creator');
  }

  /**
   * Clear role cache (useful after role changes)
   */
  clearCache() {
    this.cachedRole = null;
    this.cacheTimestamp = 0;
  }

  /**
   * Get all permissions for a role (for debugging)
   */
  static getPermissionsForRole(role: Role): Permission[] {
    return Array.from(PermissionManager.PERMISSIONS[role]);
  }

  /**
   * Add a permission to a role (for runtime configuration)
   */
  static addPermission(role: Role, permission: Permission) {
    PermissionManager.PERMISSIONS[role].add(permission);
  }

  /**
   * Remove a permission from a role (for runtime configuration)
   */
  static removePermission(role: Role, permission: Permission) {
    PermissionManager.PERMISSIONS[role].delete(permission);
  }
}

// Singleton instance
export const permissionManager = new PermissionManager();

// Export for debugging
if (typeof window !== 'undefined') {
  (window as any).permissionManager = permissionManager;
} 